import apiClient from './apiClient';
import { getApiBaseUrl } from '@/config/environment';
import { adminAuthHeaders } from '../utils/adminAuthHeaders';

// Driver interfaces matching the backend models
export interface Driver {
  id: string;
  userId: string;
  driverNumber: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  dateOfBirth: Date;
  address: string;
  licenseNumber: string;
  licenseClass: string;
  licenseExpiry: Date;
  licenseIssuedBy: string;
  vehicleTypes: string[];
  services: string[];
  bio?: string;
  languages: string[];
  profilePicture?: string;
  status: 'online' | 'offline' | 'busy' | 'inactive' | 'suspended' | 'pending' | 'active';
  applicationStatus: 'pending' | 'under_review' | 'approved' | 'rejected';
  rating: number;
  totalRatings: number;
  totalTrips: number;
  totalEarnings: number;
  availableBalance: number;
  isOnline: boolean;
  isAvailable: boolean;
  currentLocation?: {
    latitude: number;
    longitude: number;
    address?: string;
  };
  lastActiveAt: Date;
  serviceAreas: string[]; // e.g., ['Lagos', 'Lekki', 'Victoria Island']
  workingDays: string[]; // e.g., ['monday', 'tuesday', 'wednesday', 'thursday', 'friday']
  workingHours: {
    start: string; // e.g., '08:00'
    end: string;   // e.g., '20:00'
  };
  workingHoursDetailed?: {
    [key: string]: {
      start: string;
      end: string;
      enabled: boolean;
    };
  };
  emergencyContact: {
    name: string;
    phone: string;
    relationship: string;
  };
  bankDetails: {
    accountNumber: string;
    routingNumber: string;
    bankName: string;
    accountHolderName: string;
  };
  createdAt: Date;
  updatedAt: Date;
}

export interface DriverStats {
  totalDrivers: number;
  activeDrivers: number;
  onlineDrivers: number;
  pendingApplications: number;
  averageRating: number;
  totalTripsCompleted: number;
  totalEarnings: number;
}

export interface CreateDriverData {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  dateOfBirth: string;
  address: string;
  licenseNumber: string;
  licenseClass: string;
  licenseExpiry: string;
  licenseIssuedBy: string;
  vehicleTypes: string[];
  services: string[];
  bio?: string;
  languages: string[];
  emergencyContact: {
    name: string;
    phone: string;
    relationship: string;
  };
}

export interface UpdateDriverData {
  firstName?: string;
  lastName?: string;
  phone?: string;
  address?: string;
  vehicleTypes?: string[];
  services?: string[];
  bio?: string;
  languages?: string[];
  serviceAreas?: string[];
  workingDays?: string[];
  workingHours?: {
    start: string;
    end: string;
  };
  workingHoursDetailed?: {
    [key: string]: {
      start: string;
      end: string;
      enabled: boolean;
    };
  };
  emergencyContact?: {
    name: string;
    phone: string;
    relationship: string;
  };
  bankDetails?: {
    accountNumber: string;
    routingNumber: string;
    bankName: string;
    accountHolderName: string;
  };
}

export interface DriverFilters {
  search?: string;
  status?: string[];
  applicationStatus?: string[];
}

export interface PaginatedDriverResponse {
  drivers: Driver[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
  stats: DriverStats;
}

class DriverService {
  private baseUrl = '/api/v1/admin/drivers';  // Updated to use admin drivers endpoint

  // Use dynamic environment-aware API base URL
  private get adminApiBase(): string {
    return getApiBaseUrl();
  }

  // Get all drivers with filters and pagination
  async getDrivers(filters: DriverFilters = {}, page = 1, limit = 20): Promise<PaginatedDriverResponse> {
    try {
      // Build query parameters
      const params = new URLSearchParams();
      if (filters.status) params.append('status', filters.status);
      if (page) params.append('page', page.toString());
      if (limit) params.append('limit', limit.toString());

      // Use the v1 admin drivers endpoint (backend confirmed this is the correct endpoint)
      const queryString = params.toString();
      const url = `${this.adminApiBase}/api/v1/admin/drivers${queryString ? `?${queryString}` : ''}`;

      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`API Error ${response.status}`);
      }

      const apiResult = await response.json();

      if (!apiResult.success) {
        throw new Error(apiResult.error || 'Failed to fetch drivers');
      }

      // Handle both response formats:
      // 1. New format: { data: { drivers: [...] } }
      // 2. Old format: { data: { approvals: [...] } }
      const rawDrivers = apiResult.data.drivers || apiResult.data.approvals || [];

      // Transform to Driver format matching DriverManagementPage interface
      const drivers = rawDrivers.map((item: any) => {

        // Use personalInfo and contactInfo if they exist (new format), fallback to old format
        const firstName = item.personalInfo?.firstName || item.firstName || 'N/A';
        const lastName = item.personalInfo?.lastName || item.lastName || '';
        const email = item.contactInfo?.email || item.email || '';
        const phone = item.contactInfo?.phoneNumber || item.contactInfo?.phone || item.phone || '';

        // Find documents by type (if they exist)
        const driverLicenseDoc = item.documents?.find((doc: any) => doc.type === 'driver_license');
        const selfieDoc = item.documents?.find((doc: any) => doc.type === 'selfie');

        try {
          // Backend expects driver_id (like "DRV-MH7JPP88DB79D6") or driver UUID (like "41861e22-fdd0-414e-8e2d-6f93384d5d95")
          // NOT user_id (which looks like "1573806f-c76a-4989-9ba8-864558e963e6")
          const actualDriverId = item.driverId || item.driver_id || item.id;

          return {
            id: actualDriverId,
            driverCode: item.driverId || item.driverCode || item.driverNumber || `DR-${(actualDriverId || '').slice(-6).toUpperCase()}`,
            isActive: item.isActive !== undefined ? item.isActive : item.is_active,
            personalInfo: {
              firstName,
              lastName,
              email,
              phone,
              dateOfBirth: item.personalInfo?.dateOfBirth || item.dateOfBirth || '',
              address: item.personalInfo?.address || item.address || '',
              city: item.personalInfo?.city || '',
              state: item.personalInfo?.state || '',
              zipCode: item.personalInfo?.zipCode || '',
              avatar: item.personalInfo?.profilePicture || item.profilePicture || selfieDoc?.url || ''
            },
          driverDetails: {
            licenseNumber: driverLicenseDoc?.number || 'N/A',
            licenseExpiry: '', // Not provided in current API response
            yearsExperience: item.driverDetails?.experienceYears ||
                            item.driverDetails?.experience_years ||
                            item.experienceYears ||
                            item.experience_years ||
                            0,
            specializations: item.driverDetails?.specializations || (item.driverDetails?.primaryServiceType ? [item.driverDetails.primaryServiceType] : []),
            emergencyContact: item.driverDetails?.emergencyContact ? {
              name: item.driverDetails.emergencyContact.name || item.driverDetails.emergencyContact,
              phone: item.driverDetails.emergencyContact.phone || item.driverDetails.emergencyContact,
              relationship: item.driverDetails.emergencyContact.relationship || 'Emergency Contact'
            } : {
              name: '',
              phone: '',
              relationship: ''
            },
            description: item.driverDetails?.description || '',
            serviceLocations: item.driverDetails?.serviceLocations || [],
            hireConfirmationMethods: item.driverDetails?.hireConfirmationMethods || [],
            guarantor1: item.driverDetails?.guarantor1,
            guarantor2: item.driverDetails?.guarantor2,
            hourlyRate: parseFloat(item.rates?.hourly || item.driverDetails?.hourlyRate || item.driverDetails?.hourly_rate || item.hourlyRate || item.hourly_rate) || 0,
            dailyRate: parseFloat(item.rates?.daily || item.driverDetails?.dailyRate || item.driverDetails?.daily_rate || item.dailyRate || item.daily_rate) || 0,
            requiresApproval: item.requiresApproval ?? item.requires_approval ?? item.driverDetails?.requiresApproval ?? item.driverDetails?.requires_approval ?? false,
            licenseReusedFromGeneralVerification: item.driverDetails?.licenseReusedFromGeneralVerification ?? item.driverDetails?.license_reused_from_general_verification ?? item.licenseReusedFromGeneralVerification ?? item.license_reused_from_general_verification ?? false
          },
          verificationStatus: {
            identity: selfieDoc?.hasPhoto ? 'verified' : 'pending',
            background: 'pending', // Not provided in current API
            driving: driverLicenseDoc?.hasPhoto ? 'verified' : 'pending',
            training: 'not_required'
          },
          availability: {
            preferredSchedule: item.availability?.workingDays?.length ? 'custom' : 'flexible',
            workingHours: item.availability?.workingHours || item.driverDetails?.workingHours || {
              start: '09:00',
              end: '17:00'
            },
            maxDistance: 50,
            preferredAreas: item.serviceLocations || item.driverDetails?.serviceLocations || [],
            workingDays: item.availability?.workingDays || item.driverDetails?.workingDays || []
          },
          performance: {
            rating: 0,
            totalTrips: 0,
            completionRate: 0,
            onTimeRate: 0,
            customerRating: 0,
            earnings: parseFloat(item.driverDetails?.hourlyRate) || parseFloat(item.driverDetails?.dailyRate) || 0
          },
          onboardingProgress: {
            currentStep: 1,
            totalSteps: 5,
            completedSteps: item.status === 'approved' ? 5 : 1,
            status: item.status === 'pending_verification' ? 'in_progress' : item.status === 'approved' ? 'completed' : 'pending',
            pendingItems: item.status === 'approved' ? [] : ['Document verification', 'Background check']
          },
          // Map applicationStatus to display status (backend uses applicationStatus for approval workflow)
          // - applicationStatus='rejected' → status='rejected' (display as red)
          // - applicationStatus='pending'/'pending_verification'/'under_review' → status='pending' (display as yellow)
          // - applicationStatus='approved' → use item.status (active/inactive/etc)
          status: (() => {
            const appStatus = item.applicationStatus;
            if (appStatus === 'rejected') return 'rejected';
            if (appStatus === 'pending' || appStatus === 'pending_verification' || appStatus === 'under_review' || appStatus === 'pending_review') return 'pending';
            return item.status || 'active';
          })(),
          applicationStatus: item.applicationStatus || item.status || 'approved',
          lastActive: new Date(item.createdAt).toISOString(),
          joinedDate: new Date(item.createdAt).toISOString(),
          notes: item.reviewInfo?.adminNotes ? [item.reviewInfo.adminNotes] : [],
          adminNotes: item.reviewInfo?.adminNotes ? [item.reviewInfo.adminNotes] : [],
          documents: {
            driverLicense: driverLicenseDoc?.url,
            nin: null, // Not provided in current API
            selfie: selfieDoc?.url
          }
        };
        } catch (transformError) {
          console.error('❌ Error transforming driver:', item.id, transformError);
          return null; // Skip this driver if transformation fails
        }
      }).filter(Boolean); // Remove null entries

      // Apply status filter if provided
      let filteredDrivers = drivers;
      if (filters.search) {
        const searchLower = filters.search.toLowerCase();
        filteredDrivers = filteredDrivers.filter((d: any) =>
          d?.personalInfo?.firstName?.toLowerCase().includes(searchLower) ||
          d?.personalInfo?.lastName?.toLowerCase().includes(searchLower) ||
          d?.personalInfo?.email?.toLowerCase().includes(searchLower) ||
          d?.personalInfo?.phone?.includes(searchLower) ||
          d?.driverCode?.toLowerCase().includes(searchLower)
        );
      }
      if (filters.status?.length) {
        filteredDrivers = filteredDrivers.filter((d: any) => filters.status?.includes(d.status));
      }
      if (filters.applicationStatus?.length) {
        filteredDrivers = filteredDrivers.filter((d: any) => filters.applicationStatus?.includes(d.applicationStatus));
      }

      const stats = apiResult.data.statistics || {
        total_drivers: 0,
        approved: 0,
        pending: 0,
        rejected: 0,
        active_today: 0
      };

      return {
        drivers: filteredDrivers,
        pagination: {
          page,
          limit,
          total: filteredDrivers.length,
          pages: Math.ceil(filteredDrivers.length / limit)
        },
        stats: {
          totalDrivers: stats.total_drivers || 0,
          activeDrivers: stats.approved || 0,
          onlineDrivers: stats.active_today || 0,
          pendingApplications: stats.pending || 0,
          rejectedApplications: stats.rejected || 0,
          averageRating: 4.5,
          totalTripsCompleted: 0,
          totalEarnings: 0
        }
      };
    } catch (error) {
      console.error('❌ Failed to load drivers:', error);
      throw error;
    }
  }

  // Get driver by ID
  async getDriver(id: string): Promise<Driver> {
    const response = await apiClient.get(`${this.baseUrl}/${id}`);
    return response.data;
  }

  // Get complete driver details (Admin - includes guarantors, documents, all fields)
  async getDriverDetails(driverId: string): Promise<any> {
    const url = `${this.adminApiBase}/api/v1/admin/drivers/${driverId}/details`;

    const response = await fetch(url, {
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('token')}`,
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || `Failed to fetch driver details: ${response.status}`);
    }

    const result = await response.json();
    return result.data;
  }

  // Create new driver
  async createDriver(driverData: CreateDriverData): Promise<Driver> {
    const response = await apiClient.post(this.baseUrl, driverData);
    return response.data;
  }

  // Update driver
  async updateDriver(id: string, driverData: any): Promise<Driver> {
    // Use the correct admin endpoint: PATCH /api/v1/admin/drivers/{driverId}
    const url = `${this.adminApiBase}/api/v1/admin/drivers/${id}`;

    const response = await fetch(url, {
      method: 'PATCH',
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('token')}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(driverData)
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || `Failed to update driver: ${response.status}`);
    }

    const result = await response.json();
    return result.data;
  }

  // Delete driver
  async deleteDriver(id: string): Promise<void> {
    await apiClient.delete(`${this.baseUrl}/${id}`);
  }

  // Approve driver application
  async approveDriver(id: string, note?: string): Promise<any> {
    // Use original working endpoint: PATCH /api/v1/admin/drivers/{driverId}/status
    const url = `${this.adminApiBase}/api/v1/admin/drivers/${id}/status`;

    const response = await fetch(url, {
      method: 'PATCH',
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('token')}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        status: 'approved',
        adminNotes: note || undefined
      })
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || `Failed to approve driver: ${response.status}`);
    }

    return response.json();
  }

  // Reject driver application
  async rejectDriver(id: string, reason: string, adminNotes?: string): Promise<any> {
    // Use original working endpoint: PATCH /api/v1/admin/drivers/{driverId}/status
    const url = `${this.adminApiBase}/api/v1/admin/drivers/${id}/status`;

    const response = await fetch(url, {
      method: 'PATCH',
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('token')}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        status: 'rejected',
        rejectionReason: reason,
        adminNotes: adminNotes || undefined
      })
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || `Failed to reject driver: ${response.status}`);
    }

    return response.json();
  }

  // Suspend driver (Admin)
  async suspendDriver(driverId: string, reason: string, adminUserId: string, notes?: string): Promise<any> {
    const url = `${this.adminApiBase}/api/admin/drivers/${driverId}/suspend`;

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('token')}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ adminUserId, reason, notes })
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to suspend driver');
    }

    return response.json();
  }

  // Blacklist driver (Admin)
  // SECURITY UPDATE (Nov 17, 2025): NIN no longer required - backend auto-retrieves it
  async blacklistDriver(driverId: string, reason: string, adminUserId: string, notes?: string): Promise<any> {
    const url = `${this.adminApiBase}/api/admin/drivers/${driverId}/blacklist`;

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('token')}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ adminUserId, reason, notes })
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to blacklist driver');
    }

    return response.json();
  }

  // Update driver details (Admin)
  async updateDriverDetails(driverId: string, updates: {
    hourly_rate?: number;
    daily_rate?: number;
    experience_years?: number;
    primary_service_type?: string;
    driver_description?: string;
    service_locations?: any[];
    specializations?: any[];
    working_days?: any[];
    working_hours?: any;
    driver_license_number?: string;
  }): Promise<any> {
    const url = `${this.adminApiBase}/api/v1/admin/drivers/${driverId}`;


    const response = await fetch(url, {
      method: 'PATCH',
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('token')}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(updates)
    });


    if (!response.ok) {
      const error = await response.json();
      console.error('❌ API Error:', error);
      throw new Error(error.error || 'Failed to update driver');
    }

    const result = await response.json();
    return result;
  }

  // Reactivate driver
  async reactivateDriver(id: string): Promise<Driver> {
    const response = await apiClient.post(`${this.baseUrl}/${id}/reactivate`);
    return response.data;
  }

  // Update driver status (online/offline/busy)
  async updateDriverStatus(id: string, status: 'online' | 'offline' | 'busy'): Promise<Driver> {
    const response = await apiClient.patch(`${this.baseUrl}/${id}/status`, { status });
    return response.data;
  }

  // Update driver availability
  async updateDriverAvailability(id: string, isAvailable: boolean): Promise<Driver> {
    const response = await apiClient.patch(`${this.baseUrl}/${id}/availability`, { isAvailable });
    return response.data;
  }

  // Update driver location
  async updateDriverLocation(id: string, location: { latitude: number; longitude: number; address?: string }): Promise<Driver> {
    const response = await apiClient.patch(`${this.baseUrl}/${id}/location`, { location });
    return response.data;
  }

  // Get driver statistics
  async getDriverStats(): Promise<DriverStats> {
    const response = await apiClient.get(`${this.baseUrl}/stats`);
    return response.data;
  }

  // Get driver earnings
  async getDriverEarnings(id: string, period?: string): Promise<any> {
    const params = period ? `?period=${period}` : '';
    const response = await apiClient.get(`${this.baseUrl}/${id}/earnings${params}`);
    return response.data;
  }

  // Get driver trips
  async getDriverTrips(id: string, page = 1, limit = 20): Promise<any> {
    const response = await apiClient.get(`${this.baseUrl}/${id}/trips?page=${page}&limit=${limit}`);
    return response.data;
  }

  // Upload driver document
  async uploadDriverDocument(id: string, file: File, type: string): Promise<any> {
    const formData = new FormData();
    formData.append('document', file);
    formData.append('type', type);
    
    const response = await apiClient.post(`${this.baseUrl}/${id}/documents`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data;
  }

  // Get driver documents
  async getDriverDocuments(id: string): Promise<any[]> {
    const response = await apiClient.get(`${this.baseUrl}/${id}/documents`);
    return response.data;
  }

  // Update driver document status
  async updateDocumentStatus(id: string, documentId: string, status: 'pending' | 'approved' | 'rejected', reason?: string): Promise<any> {
    const response = await apiClient.patch(`${this.baseUrl}/${id}/documents/${documentId}`, { status, reason });
    return response.data;
  }

  // Add admin note to driver
  async addAdminNote(driverId: string, note: { type: string; note: string; isPrivate: boolean }): Promise<any> {
    const url = `${this.adminApiBase}/api/admin/drivers/${driverId}/notes`;


    const response = await fetch(url, {
      method: 'POST',
      headers: adminAuthHeaders(),
      body: JSON.stringify(note)
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => null);
      throw new Error(errorData?.message || `Failed to add note: ${response.statusText}`);
    }

    const data = await response.json();
    return data;
  }

  // Get driver admin notes
  async getAdminNotes(driverId: string): Promise<any[]> {
    const response = await apiClient.get(`${this.baseUrl}/${driverId}/notes`);
    return response.data;
  }

  // Update driver profile picture
  async updateDriverProfilePicture(driverId: string, profilePicture: string): Promise<any> {
    // Use RESTful endpoint (recommended by backend)
    const url = `${this.adminApiBase}/api/v1/admin/drivers/${driverId}/profile-picture`;

    const response = await fetch(url, {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('token')}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        profilePicture: profilePicture  // Must be base64 string starting with "data:image/"
      })
    });

    if (!response.ok) {
      throw new Error(`Failed to update profile picture: ${response.status}`);
    }

    const result = await response.json();

    if (!result.success) {
      throw new Error(result.error || 'Failed to update profile picture');
    }

    return result.data;
  }

  // Convert backend Driver to frontend SampleDriver format for compatibility
  convertToSampleDriver(driver: Driver): any {
    return {
      id: driver.id,
      driverCode: driver.driverNumber,
      personalInfo: {
        firstName: driver.firstName,
        lastName: driver.lastName,
        email: driver.email,
        phone: driver.phone,
        location: driver.address,
      },
      licenseInfo: {
        licenseNumber: driver.licenseNumber,
        state: driver.licenseIssuedBy,
        expiryDate: new Date(driver.licenseExpiry).toLocaleDateString(),
      },
      status: this.mapBackendStatusToFrontend(driver.status),
      verificationStatus: this.mapApplicationStatusToVerification(driver.applicationStatus),
      currentVehicle: undefined, // TODO: Implement vehicle assignment
      rating: driver.rating,
      totalTrips: driver.totalTrips,
      totalEarnings: driver.totalEarnings,
      availability: driver.isAvailable ? 'Available' : 'Unavailable',
      lastActivity: new Date(driver.lastActiveAt).toLocaleDateString(),
      joinedDate: new Date(driver.createdAt).toLocaleDateString(),
      isOnline: driver.isOnline,
      bio: driver.bio,
      languages: driver.languages,
      services: driver.services,
      workingHours: driver.workingHours,
      emergencyContact: driver.emergencyContact,
      bankDetails: driver.bankDetails,
    };
  }

  private mapBackendStatusToFrontend(status: string): 'Active' | 'Pending' | 'Suspended' | 'Inactive' {
    switch (status) {
      case 'active':
      case 'online':
      case 'busy':
        return 'Active';
      case 'pending':
        return 'Pending';
      case 'suspended':
        return 'Suspended';
      case 'offline':
      case 'inactive':
      default:
        return 'Inactive';
    }
  }

  private mapApplicationStatusToVerification(applicationStatus: string): 'Verified' | 'Pending' | 'Rejected' {
    switch (applicationStatus) {
      case 'approved':
        return 'Verified';
      case 'rejected':
        return 'Rejected';
      case 'pending':
      case 'under_review':
      default:
        return 'Pending';
    }
  }

  // Convert user object to frontend driver format (with all required fields)
  private convertUserToDriver(user: any): any {
    return {
      id: user.id || Math.random().toString(),
      driverCode: user.driverNumber || `DR${Math.floor(Math.random() * 10000)}`,
      personalInfo: {
        firstName: user.firstName || user.name?.split(' ')[0] || 'John',
        lastName: user.lastName || user.name?.split(' ')[1] || 'Doe',
        email: user.email || 'driver@example.com',
        phone: user.phone || '+1234567890',
        location: user.address || '123 Main St',
        avatar: user.profilePicture || user.avatar,
      },
      licenseInfo: {
        licenseNumber: user.licenseNumber || 'DL123456',
        state: user.licenseIssuedBy || 'DMV',
        expiryDate: user.licenseExpiry ? new Date(user.licenseExpiry).toLocaleDateString() : '12/31/2025',
      },
      status: this.mapBackendStatusToFrontend(user.status || 'active'),
      verificationStatus: this.mapApplicationStatusToVerification(user.applicationStatus || 'approved'),
      currentVehicle: undefined,
      rating: user.rating || 4.5,
      totalTrips: user.totalTrips || 25,
      totalEarnings: user.totalEarnings || 1500,
      availability: user.isAvailable ? 'Available' : 'Unavailable',
      lastActivity: user.lastActiveAt ? new Date(user.lastActiveAt).toLocaleDateString() : new Date().toLocaleDateString(),
      joinedDate: user.createdAt ? new Date(user.createdAt).toLocaleDateString() : new Date().toLocaleDateString(),
      isOnline: user.isOnline || Math.random() > 0.5,
      bio: user.bio || '',
      languages: user.languages || ['English'],
      services: user.services || ['ride-share'],
      workingHours: user.workingHours || this.getDefaultWorkingHours(),
      emergencyContact: user.emergencyContact || this.getDefaultEmergencyContact(),
      bankDetails: user.bankDetails || this.getDefaultBankDetails(),
      preferences: {
        notifications: {
          email: true,
          sms: true,
          push: true,
        },
        workingHours: {
          start: '09:00',
          end: '17:00',
        },
        maxDistance: 30,
        preferredAreas: ['Downtown', 'Airport'],
      },
      performance: {
        rating: user.rating || 4.5,
        totalTrips: user.totalTrips || 25,
        completionRate: 95,
        onTimeRate: 92,
        customerRating: user.rating || 4.5,
        earnings: user.totalEarnings || 1500,
      },
      onboardingProgress: {
        currentStep: Math.floor(Math.random() * 6) + 1,
        completedSteps: ['personal_info', 'license', 'background_check'],
        pendingItems: this.generatePendingItems(),
        lastFollowUp: Math.random() > 0.5 ? new Date(Date.now() - Math.random() * 7 * 24 * 60 * 60 * 1000).toISOString() : undefined,
        nextFollowUpDue: Math.random() > 0.7 ? new Date(Date.now() + Math.random() * 7 * 24 * 60 * 60 * 1000).toISOString() : undefined,
      },
      adminNotes: [],
      pricing: {
        daily: user.dailyRate ?? 20000,
        hourly: user.hourlyRate ?? 2500
      }
    };
  }

  private getDefaultWorkingHours() {
    return {
      monday: { start: '09:00', end: '17:00', enabled: true },
      tuesday: { start: '09:00', end: '17:00', enabled: true },
      wednesday: { start: '09:00', end: '17:00', enabled: true },
      thursday: { start: '09:00', end: '17:00', enabled: true },
      friday: { start: '09:00', end: '17:00', enabled: true },
      saturday: { start: '09:00', end: '15:00', enabled: true },
      sunday: { start: '10:00', end: '14:00', enabled: false }
    };
  }

  private getDefaultEmergencyContact() {
    return {
      name: 'Emergency Contact',
      phone: '+1234567890',
      relationship: 'Family'
    };
  }

  private getDefaultBankDetails() {
    return {
      accountNumber: '****1234',
      routingNumber: '123456789',
      bankName: 'Bank Name',
      accountHolderName: 'Account Holder'
    };
  }

  private generatePendingItems() {
    const possibleItems = [
      { item: 'Background Check', description: 'Complete background verification', urgency: 'high' as const, dueDate: new Date(Date.now() + 2 * 24 * 60 * 60 * 1000).toISOString() },
      { item: 'Vehicle Inspection', description: 'Submit vehicle inspection report', urgency: 'medium' as const, dueDate: new Date(Date.now() + 5 * 24 * 60 * 60 * 1000).toISOString() },
      { item: 'Insurance Documents', description: 'Upload current insurance policy', urgency: 'high' as const, dueDate: new Date(Date.now() + 1 * 24 * 60 * 60 * 1000).toISOString() },
      { item: 'Driver Training', description: 'Complete online driver training course', urgency: 'low' as const, dueDate: new Date(Date.now() + 10 * 24 * 60 * 60 * 1000).toISOString() },
      { item: 'Profile Photo', description: 'Upload professional profile photo', urgency: 'low' as const, dueDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString() },
    ];

    // Randomly select 0-3 pending items
    const numItems = Math.floor(Math.random() * 4);
    const selectedItems: any[] = [];
    for (let i = 0; i < numItems; i++) {
      const randomItem = possibleItems[Math.floor(Math.random() * possibleItems.length)];
      if (!selectedItems.find(item => item.item === randomItem.item)) {
        selectedItems.push(randomItem);
      }
    }
    return selectedItems;
  }

  // ============= Guarantor Management =============

  /**
   * Get guarantor status for a driver
   * @param driverId - Driver ID (e.g., DRV-ABC123)
   * @returns Guarantor information with verification status
   */
  async getGuarantors(driverId: string): Promise<GuarantorResponse> {
    const url = `${this.adminApiBase}/api/v1/admin/drivers/${driverId}/guarantors`;


    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('token')}`,
        'Content-Type': 'application/json'
      }
    });


    if (!response.ok) {
      const errorText = await response.text();
      console.error('❌ Failed to fetch guarantors:', errorText);
      throw new Error(`Failed to fetch guarantors: ${response.status}`);
    }

    const result = await response.json();

    if (!result.success) {
      console.error('❌ API returned error:', result.error);
      throw new Error(result.error || 'Failed to fetch guarantors');
    }

    return result.data;
  }

  /**
   * Resend verification SMS to a guarantor
   * @param driverId - Driver ID (e.g., DRV-ABC123)
   * @param guarantorNumber - Guarantor number (1 or 2)
   * @returns Success message
   */
  async resendGuarantorSMS(driverId: string, guarantorNumber: 1 | 2): Promise<{ message: string }> {
    const url = `${this.adminApiBase}/api/v1/admin/drivers/${driverId}/guarantors`;

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('token')}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ guarantorNumber })
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || `Failed to resend SMS: ${response.status}`);
    }

    const result = await response.json();

    if (!result.success) {
      throw new Error(result.error || 'Failed to resend SMS');
    }

    return { message: result.message };
  }
}

// ============= Guarantor Types =============

export interface Guarantor {
  guarantorNumber: 1 | 2;
  name: string;
  phone: string;
  relationship: string;
  status: 'verified' | 'pending' | 'declined' | 'expired';
  verifiedAt: string | null;
  smsSent: boolean;
  smsSentAt: string | null;
}

export interface GuarantorResponse {
  guarantors: Guarantor[];
}

// Toggle driver active/inactive status
export interface ToggleDriverActiveRequest {
  is_active: boolean;
  reason?: string;
}

export interface ToggleDriverActiveResponse {
  success: boolean;
  message?: string;
  data?: {
    driverId: string;
    driverName: string;
    isActive: boolean;
    previousState: boolean;
    updatedAt: string;
  };
  error?: {
    code: string;
    message: string;
  };
}

/**
 * Toggle driver active/inactive status
 * PUT /api/v1/admin/drivers/{driverId}/toggle-active
 */
export async function toggleDriverActive(
  driverId: string,
  data: ToggleDriverActiveRequest
): Promise<ToggleDriverActiveResponse> {
  try {
    const apiBaseUrl = getApiBaseUrl();
    const url = `${apiBaseUrl}/api/v1/admin/drivers/${driverId}/toggle-active`;

    const response = await fetch(url, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('token')}`,
      },
      body: JSON.stringify(data),
    });


    const result = await response.json();


    if (!response.ok) {
      console.error('❌ toggleDriverActive failed:', {
        status: response.status,
        statusText: response.statusText,
        error: result.error,
        fullResult: result
      });
      throw new Error(result.error?.message || 'Failed to toggle driver status');
    }

    return result;
  } catch (error) {
    console.error('❌ Error toggling driver status:', error);
    throw error;
  }
}

export const driverService = new DriverService();
export default driverService;